import React, { useState, useCallback, useMemo, useContext, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { DropdownOption } from '../../dropdown/types';
import { MenuItem } from '../../dropdown/MenuItem';
import { SimpleInputChip } from './SimpleInputChip';
import { withCssSelector } from '../../shared/withCssSelector';
import { Loader } from '../../loader';
import { DropdownParentContext } from '../../dropdown/DropdownParentContext';
import { useDropdownLogic } from '../../dropdown/useDropdownLogic';
import {
	StyledSelectInput,
	StyledChevron,
	StyledSelectionItems,
	StyledPlaceholder,
	SearchInput,
	MultiSelectMenuRoot,
	MultiSelectMenuOverflow,
} from './MultiSelectDropdown.style';

type MultiSelectDropdownProps = {
	className?: string;
	options: string[] | DropdownOption<string>[];
	selectedValues: string[];
	onChange: (values: string[]) => void;
	placeholder?: string;
	searchPlaceholder?: string;
	loadingPlaceholder?: string;
	noMatchesPlaceholder?: string;
	noOptionsPlaceholder?: string;
	disabled?: boolean;
	searchable?: boolean;
	canRemove?: boolean;
	size?: 'default' | 'small';
	hasError?: boolean;
	loading?: boolean;
};

export const MultiSelectDropdown = withCssSelector(
	React.forwardRef<HTMLDivElement, MultiSelectDropdownProps>(function MultiSelectDropdown({
		className,
		options,
		selectedValues,
		onChange,
		placeholder,
		searchPlaceholder,
		loadingPlaceholder,
		noMatchesPlaceholder = '',
		noOptionsPlaceholder = '',
		disabled,
		searchable = false,
		canRemove = true,
		size = 'default',
		hasError = false,
		loading = false,
	}, ref) {
		const [searchQuery, setSearchQuery] = useState('');
		const hasSelection = selectedValues.length > 0;
		const displayPlaceholder = loading ? loadingPlaceholder : placeholder;

		const onMenuClose = useCallback(() => {
			setSearchQuery('');
		}, []);

		const {
			isOpen,
			styles,
			attributes,
			onClose,
			onOpen,
			buttonRef,
			menuRef,
		} = useDropdownLogic({ onMenuClose, sticky: true });

		// Merge external ref with internal buttonRef
		const mergedRef = useCallback((node: HTMLDivElement | null) => {
			// Set internal ref for Popper
			if (typeof buttonRef === 'function') {
				buttonRef(node);
			} else if (buttonRef) {
				(buttonRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
			}

			// Set external ref if provided
			if (ref) {
				if (typeof ref === 'function') {
					ref(node);
				} else {
					(ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
				}
			}
		}, [buttonRef, ref]);

		// Normalize options to DropdownOption format
		const normalizedOptions: DropdownOption<string>[] = useMemo(() => 
			options.map(option => {
				if (typeof option === 'string') {
					return { value: option, label: option };
				}
				return option;
			}),
		[options]);

		// Filter out already selected options and apply search
		const availableOptions = useMemo(() => {
			let filtered = normalizedOptions.filter(
				option => !selectedValues.includes(option.value)
			);

			if (searchable && searchQuery) {
				filtered = filtered.filter(option =>
					option.label.toLowerCase().includes(searchQuery.toLowerCase())
				);
			}

			return filtered;
		}, [normalizedOptions, selectedValues, searchable, searchQuery]);

		// Auto-close menu when no options are available (unless searchable)
		useEffect(() => {
			if (isOpen && availableOptions.length === 0 && !searchable) {
				onClose();
			}
		}, [availableOptions.length, isOpen, searchable, onClose]);

		// Reposition menu when selected values change (button height may change)
		useEffect(() => {
			if (isOpen && window.requestAnimationFrame) {
				// Use requestAnimationFrame to ensure DOM has updated
				window.requestAnimationFrame(() => {
					// Trigger Popper update by dispatching a resize event
					window.dispatchEvent(new Event('resize'));
				});
			}
		}, [selectedValues, isOpen]);

		const handleRemove = useCallback((value: string) => {
			const newValues = selectedValues.filter(v => v !== value);
			onChange(newValues);
		}, [selectedValues, onChange]);

		const handleSelect = useCallback((value: string) => {
			if (!selectedValues.includes(value)) {
				const newValues = [...selectedValues, value];
				onChange(newValues);
				setSearchQuery('');
			}
		}, [selectedValues, onChange]);

		const handleInputClick = useCallback(() => {
			if (!disabled && !loading) {
				if (isOpen) {
					onClose();
				} else {
					// Only open if there are available options or search is enabled
					if (availableOptions.length > 0 || searchable) {
						onOpen();
					}
				}
			}
		}, [disabled, loading, isOpen, onClose, onOpen, availableOptions.length, searchable]);

		const { parent } = useContext(DropdownParentContext);

		return (
			<>
				<StyledSelectInput
					onClick={handleInputClick}
					$disabled={disabled}
					$active={isOpen}
					$size={size}
					$hasError={hasError}
					$hasContent={hasSelection}
					className={className}
					ref={mergedRef}
				>
					{!hasSelection ?
						(displayPlaceholder ?
							<StyledPlaceholder $size={size}>
								{displayPlaceholder}
							</StyledPlaceholder>
							: null) :
						<StyledSelectionItems>
							{selectedValues.map(value => {
								const option = normalizedOptions.find(opt => opt.value === value);
								return (
									<SimpleInputChip
										key={value}
										value={value}
										label={option?.label}
										icon={option?.icon}
										onRemove={handleRemove}
										canRemove={canRemove && !disabled}
										size={size}
									/>
								);
							})}
						</StyledSelectionItems>}
					{loading ? <Loader /> : <StyledChevron 
						icon={'dropdown-chevron'} 
						onClick={isOpen ? onClose : undefined} 
					/>}
				</StyledSelectInput>
				{isOpen && createPortal(
					<MultiSelectMenuRoot 
						style={styles} 
						{...(attributes || {})} 
						ref={menuRef}
					>
						<MultiSelectMenuOverflow>
							{searchable && (
								<SearchInput
									type="text"
									value={searchQuery}
									onChange={(e) => setSearchQuery(e.target.value)}
									placeholder={searchPlaceholder}
									autoFocus
								/>
							)}
							{availableOptions.length === 0 ? (
								<MenuItem
									value=""
									label={searchQuery ? noMatchesPlaceholder : noOptionsPlaceholder}
									disabled
								/>
							) : (
								availableOptions.map(option => (
									<MenuItem
										key={option.value}
										value={option.value}
										label={option.label}
										icon={option.icon}
										onSelect={() => handleSelect(option.value)}
										disabled={option.disabled}
									/>
								))
							)}
						</MultiSelectMenuOverflow>
					</MultiSelectMenuRoot>,
					parent || document.body
				)}
			</>
		);
	})
	, StyledSelectInput.toString());

